import { GTAO_DENOISE } from "./ambientOcclusionSettings";

const PATCH_MARKER = "// PRODUCT_VIEWER_GTAO_DENOISE_V1";
const MAX_DENOISE_RADIUS = 64;
const REGEXP_ESCAPE_REPLACEMENT = String.raw`\$&`;
const ANCHOR_WHITESPACE = String.raw`\s+`;

/** Round/clamp debug input; non-finite requests restore the default. */
export function clampGtaoDenoisePasses(count: number): 1 | 2 {
  if (!Number.isFinite(count)) return GTAO_DENOISE.passes;
  return count < 1.5 ? 1 : 2;
}

/** Upstream's radial ladder starts at one pixel, even for radius zero. */
export function clampGtaoDenoiseRadius(radius: number): number {
  return Number.isFinite(radius)
    ? Math.min(MAX_DENOISE_RADIUS, Math.max(1, radius))
    : GTAO_DENOISE.radius;
}

const shaderPatches = [
  {
    name: "noise rotation",
    before:
      "mat2 rotationMatrix = " +
      "mat2(noiseVec.x, -noiseVec.y, noiseVec.x, noiseVec.y);",
    after: /* glsl */ `mat2 rotationMatrix = mat2(
      noiseVec.y, noiseVec.x, -noiseVec.x, noiseVec.y
    );`,
  },
  {
    name: "pixel-space offset",
    before:
      "vec2 offset = rotationMatrix * (sampleDir.xy * " +
      "(1. + sampleDir.z * (radius - 1.)) / resolution);",
    after: /* glsl */ `vec2 pixelOffset = rotationMatrix *
      (sampleDir.xy * (1. + sampleDir.z * (radius - 1.)));
    vec2 offset = pixelOffset / resolution;`,
  },
  {
    name: "neighbor rejection",
    before: /* glsl */ `void denoiseSample(in vec3 center,
      in vec3 viewNormal, in vec3 viewPos, in vec2 sampleUv,
      inout vec3 denoised, inout float totalWeight) {
      vec4 sampleTexel = textureLod(tDiffuse, sampleUv, 0.0);
      float sampleDepth = getDepth(sampleUv);
      vec3 sampleNormal = getViewNormal(sampleUv);
      vec3 neighborColor = sampleTexel.rgb;
      vec3 viewPosSample = getViewPosition(sampleUv, sampleDepth);`,
    after: /* glsl */ `void denoiseSample(in vec3 center,
      in vec3 viewNormal, in vec3 viewPos, in vec2 sampleUv,
      inout vec3 denoised, inout float totalWeight) {
      if (any(lessThan(sampleUv, vec2(0.0))) ||
          any(greaterThanEqual(sampleUv, vec2(1.0)))) return;
      float sampleDepth = getDepth(sampleUv);
      if (sampleDepth >= 1.0) return;
      vec4 sampleTexel = textureLod(tDiffuse, sampleUv, 0.0);
      vec3 sampleNormal = getViewNormal(sampleUv);
      vec3 neighborColor = sampleTexel.rgb;
      vec3 viewPosSample = getViewPosition(sampleUv, sampleDepth);`,
  },
  {
    name: "center rejection",
    before: /* glsl */ `float depth = getDepth(vUv.xy);
      vec3 viewNormal = getViewNormal(vUv);
      if (depth == 1. || dot(viewNormal, viewNormal) == 0.) {
        discard;
        return;
      }`,
    after: /* glsl */ `float depth = getDepth(vUv.xy);
      if (depth >= 1.0) {
        discard;
        return;
      }
      vec3 viewNormal = getViewNormal(vUv);
      if (dot(viewNormal, viewNormal) == 0.) {
        discard;
        return;
      }`,
  },
] as const;

// These untouched anchors establish the meaning of noiseVec and the loop.
// In particular, swapping sin/cos upstream would invalidate our matrix.
const shaderInvariants = [
  { name: "rotation index uniform", source: "uniform int index;" },
  {
    name: "sin/cos noise vector",
    source:
      "vec2 noiseVec = vec2(sin(noiseTexel[index % 4] * 2. * PI), " +
      "cos(noiseTexel[index % 4] * 2. * PI));",
  },
  {
    name: "Poisson loop",
    source:
      "for (int i = 0; i < SAMPLES; i++) { " +
      "vec3 sampleDir = poissonDisk[i];",
  },
  {
    name: "neighbor call",
    source:
      "vec2 sampleUv = vUv + offset; " +
      "denoiseSample(center, viewNormal, viewPos, sampleUv, " +
      "denoised, totalWeight);",
  },
] as const;

function anchorPattern(anchor: string): RegExp {
  // Ignore indentation/line wrapping, but not changed GLSL expressions.
  const pattern = anchor
    .trim()
    .split(/\s+/)
    .map((token) =>
      // `\$&` expansion is the point here: it escapes the matched
      // metacharacter rather than inserting text. verbatim-replace-ok
      token.replaceAll(/[.*+?^${}()|[\]\\]/g, REGEXP_ESCAPE_REPLACEMENT),
    )
    .join(ANCHOR_WHITESPACE);
  return new RegExp(pattern, "g");
}

function requireAnchorCount(
  shader: string,
  anchor: string,
  name: string,
  expected: number,
): void {
  const pattern = anchorPattern(anchor);
  let count = 0;
  while (pattern.exec(shader) !== null) count++;
  if (count !== expected) {
    throw new Error(
      "ProductViewerGTAOPass: unsupported PoissonDenoiseShader " +
        `(${name}: expected ${expected} anchor(s), found ${count}). ` +
        "Review the Three.js denoise patch before upgrading.",
    );
  }
}

/**
 * Patch an instance's fragment shader, never the shared Three.js shader.
 * GLSL mat2 constructors are column-major and noiseVec is (sin, cos).
 * Rotate in pixel space first: UV-space rotation stretches rectangular
 * buffers. Reject invalid neighbors before fetching normals/view positions.
 *
 * Both fresh and already-patched sources are validated. A partial patch or
 * upstream drift must fail loudly rather than silently changing the filter.
 */
export function patchGtaoDenoiseShader(fragmentShader: string): string {
  const patched = fragmentShader.includes(PATCH_MARKER);
  requireAnchorCount(
    fragmentShader,
    PATCH_MARKER,
    "patch marker",
    patched ? 1 : 0,
  );
  for (const { name, source } of shaderInvariants) {
    requireAnchorCount(fragmentShader, source, name, 1);
  }
  for (const { name, before, after } of shaderPatches) {
    requireAnchorCount(fragmentShader, patched ? after : before, name, 1);
    requireAnchorCount(
      fragmentShader,
      patched ? before : after,
      `${name} (mixed patch versions)`,
      0,
    );
  }
  if (patched) return fragmentShader;

  let result = fragmentShader;
  for (const { before, after } of shaderPatches) {
    result = result.replaceAll(anchorPattern(before), () => after);
  }
  return `${PATCH_MARKER}\n${result}`;
}
